import { useCallback } from "react";
import {
  useApiService,
  ApiCallOptions,
  ApiResponse,
  ApiError,
} from "../services/apiService";
import { useLoading } from "../contexts/loading-context";
import { useSnackbar } from "../contexts/snackbar-context";

export interface UseApiOptions {
  showLoader?: boolean;
  showErrorSnackbar?: boolean;
  showSuccessSnackbar?: boolean;
  throwOnError?: boolean;
  successMessage?: string;
  loadingMessage?: string;
  onSuccess?: (data: any) => void;
  onError?: (error: ApiError) => void;
  onFinally?: () => void;
}

export interface UseBlobApiOptions extends Omit<UseApiOptions, 'onSuccess'> {
  onSuccess?: (blob: Blob, contentType: string, fileName?: string) => void;
}

export const useApiWithHandling = () => {
  const apiService = useApiService();
  const { showLoading, hideLoading } = useLoading();
  const { showError, showSuccess } = useSnackbar();

  const apiCallWithHandling = useCallback(
    async <T = any,>(
      apiOptions: ApiCallOptions,
      options: UseApiOptions = {}
    ): Promise<ApiResponse<T> | null> => {
      const showLoader = options.showLoader ?? true;
      const showErrorSnackbar = options.showErrorSnackbar ?? true;
      const showSuccessSnackbar = options.showSuccessSnackbar ?? false;
      const throwOnError = options.throwOnError ?? false;
      const successMessage = options.successMessage ?? "Operation completed successfully";
      const loadingMessage = options.loadingMessage ?? "Loading...";
      const { onSuccess, onError, onFinally } = options;

      try {
        // Show loading
        if (showLoader) {
          showLoading(loadingMessage);
        }

        // Make API call
        const response = await apiService.apiCall<T>(apiOptions);

        // Handle success
        if (showSuccessSnackbar) {
          showSuccess(successMessage);
        }

        if (onSuccess) {
          onSuccess(response.data);
        }

        return response;
      } catch (error) {
        const apiError = error as ApiError;

        // Show error snackbar
        if (showErrorSnackbar) {
          showError(apiError.message || "An unexpected error occurred");
        }

        // Call custom error handler
        if (onError) {
          onError(apiError);
        }

        if (throwOnError) {
          throw apiError;
        }

        return null;
      } finally {
        // Hide loading
        if (showLoader) {
          hideLoading();
        }

        // Call custom finally handler
        if (onFinally) {
          onFinally();
        }
      }
    },
    [apiService, showLoading, hideLoading, showError, showSuccess]
  );

  // Convenience methods with handling
  const get = useCallback(
    <T = any,>(
      endpoint: string,
      options: UseApiOptions = {},
      requireAuth = true
    ) =>
      apiCallWithHandling<T>({ method: "GET", endpoint, requireAuth }, options),
    [apiCallWithHandling]
  );

  const post = useCallback(
    <T = any,>(
      endpoint: string,
      payload?: any,
      options: UseApiOptions = {},
      requireAuth = true
    ) =>
      apiCallWithHandling<T>(
        { method: "POST", endpoint, payload, requireAuth },
        options
      ),
    [apiCallWithHandling]
  );

  const put = useCallback(
    <T = any,>(
      endpoint: string,
      payload?: any,
      options: UseApiOptions = {},
      requireAuth = true
    ) =>
      apiCallWithHandling<T>(
        { method: "PUT", endpoint, payload, requireAuth },
        options
      ),
    [apiCallWithHandling]
  );

  const del = useCallback(
    <T = any,>(
      endpoint: string,
      options: UseApiOptions = {},
      requireAuth = true
    ) =>
      apiCallWithHandling<T>(
        { method: "DELETE", endpoint, requireAuth },
        options
      ),
    [apiCallWithHandling]
  );

  const patch = useCallback(
    <T = any,>(
      endpoint: string,
      payload?: any,
      options: UseApiOptions = {},
      requireAuth = true
    ) =>
      apiCallWithHandling<T>(
        { method: "PATCH", endpoint, payload, requireAuth },
        options
      ),
    [apiCallWithHandling]
  );

  // Blob-specific method with handling
  const getBlobWithHandling = useCallback(
    async (
      endpoint: string,
      options: UseBlobApiOptions = {},
      requireAuth = true
    ): Promise<{ blob: Blob; contentType: string; fileName?: string } | null> => {
      const showLoader = options.showLoader ?? true;
      const showErrorSnackbar = options.showErrorSnackbar ?? true;
      const showSuccessSnackbar = options.showSuccessSnackbar ?? false;
      const successMessage = options.successMessage ?? "File loaded successfully";
      const loadingMessage = options.loadingMessage ?? "Loading file...";
      const { onSuccess, onError, onFinally } = options;

      try {
        // Show loading
        if (showLoader) {
          showLoading(loadingMessage);
        }

        // Make blob API call
        const response = await apiService.getBlob(endpoint, requireAuth);

        // Handle success
        if (showSuccessSnackbar) {
          showSuccess(successMessage);
        }

        if (onSuccess) {
          onSuccess(response.blob, response.contentType, response.fileName);
        }

        return response;
      } catch (error) {
        const apiError = error as ApiError;

        // Show error snackbar
        if (showErrorSnackbar) {
          showError(apiError.message || "Failed to load file");
        }

        // Call custom error handler
        if (onError) {
          onError(apiError);
        }

        return null;
      } finally {
        // Hide loading
        if (showLoader) {
          hideLoading();
        }

        // Call custom finally handler
        if (onFinally) {
          onFinally();
        }
      }
    },
    [apiService, showLoading, hideLoading, showError, showSuccess]
  );

  const postBlobWithHandling = useCallback(
    async (
      endpoint: string,
      payload?: any,
      options: UseBlobApiOptions = {},
      requireAuth = true
    ): Promise<{ blob: Blob; contentType: string; fileName?: string } | null> => {
      const showLoader = options.showLoader ?? true;
      const showErrorSnackbar = options.showErrorSnackbar ?? true;
      const showSuccessSnackbar = options.showSuccessSnackbar ?? false;
      const successMessage = options.successMessage ?? "File loaded successfully";
      const loadingMessage = options.loadingMessage ?? "Loading file...";
      const { onSuccess, onError, onFinally } = options;

      try {
        if (showLoader) {
          showLoading(loadingMessage);
        }

        const response = await apiService.postBlob(endpoint, payload, requireAuth);

        if (showSuccessSnackbar) {
          showSuccess(successMessage);
        }

        if (onSuccess) {
          onSuccess(response.blob, response.contentType, response.fileName);
        }

        return response;
      } catch (error) {
        const apiError = error as ApiError;

        if (showErrorSnackbar) {
          showError(apiError.message || "Failed to load file");
        }

        if (onError) {
          onError(apiError);
        }

        return null;
      } finally {
        if (showLoader) {
          hideLoading();
        }

        if (onFinally) {
          onFinally();
        }
      }
    },
    [apiService, showLoading, hideLoading, showError, showSuccess]
  );

  return {
    apiCall: apiCallWithHandling,
    get,
    post,
    put,
    del,
    patch,
    getBlob: getBlobWithHandling,
    postBlob: postBlobWithHandling,
  };
};
